import { ENDPOINTS, QUERY_KEY } from '@/constants';
import { useQuery } from '@tanstack/react-query';
import { notFound } from 'next/navigation';
import { useEffect } from 'react';
import { sendSentryError } from '@lib/sentry';

const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * CSV 데이터를 가져와 텍스트로 반환하는 함수
 */
const fetchGeneCoverageData = async (): Promise<string> => {
  try {
    const response = await fetch(ENDPOINTS.geneCoverageData);
    if (!response.ok) {
      throw new Error(
        `Failed to fetch gene coverage data: ${response.statusText}`,
      );
    }
    const csvText = await response.text();
    return csvText;
  } catch (error) {
    sendSentryError({
      error,
      source: 'gene-coverage-browser-fetch-gene-coverage-data',
    });
    throw error;
  }
};

/**
 * 유전자 커버리지 데이터를 가져오고 관리하는 기능
 *
 * 주요 기능:
 * 1. CSV 파일을 비동기적으로 가져오는 함수 (fetchGeneCoverageData)
 * 2. Papa Parse를 사용한 CSV 파일 파싱 및 데이터 변환
 * 3. React Query를 활용한 데이터 캐싱 및 상태 관리
 * 4. 데이터 로딩 상태 및 오류 처리
 *
 * @returns {Object} 유전자 커버리지 데이터 관련 상태와 함수
 * @returns {string} data - CSV 텍스트 데이터
 * @returns {boolean} isLoading - 데이터 로딩 중 여부
 * @returns {boolean} isError - 데이터 로딩 오류 발생 여부
 * @returns {Error|null} error - 발생한 오류 객체
 */

export const useGeneCoverageData = () => {
  const result = useQuery<string, Error>({
    queryKey: [QUERY_KEY.geneCoverageData],
    queryFn: fetchGeneCoverageData,
    staleTime: ONE_WEEK_IN_MS,
    gcTime: ONE_WEEK_IN_MS,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    meta: {
      persist: true,
    },
  });

  useEffect(() => {
    if (result.error) {
      notFound();
    }
  }, [result.error]);

  return result;
};
